import { toursApi } from "@api";
import { shouldCheckAvailabilityByGroup } from "./availabilityEndpoint";
import {
    BOOKING_SYSTEMS_WITH_CUSTOM_BOOKING_TIME_IDS,
    CHECK_AVAILABILITY_START_DATE_FORMAT,
    FARE_PRICE_TRAVEL_DATE_FORMAT,
} from "@constants";
import {
    BaseAvailability,
    IAvailabilityDetailsToConfirm,
    IAvailField,
    IAvailOptions,
    IAvailabilityByRange,
    ICustomerInfo,
    IFareCart,
    IPriceGroup,
    IPricingInfo,
    IProductAvailability,
    IProductAvailDropOffOption,
    IProductAvailPickUpOption,
    IProductAvailTimeOption,
    ISetCustomerData,
    ITourFare,
    ITourFareBookingTime,
    AvailabilityType,
    IAvailabilityByProductAndRange,
    IBookingTimeData,
    SelectedAvailability,
    IBookingAvailability,
    CheckProductAvailsDateRange,
    CheckProductAvailsData,
    ICheckAvailabilityProduct,
    ICheckAvailProductFare,
    ICheckAvailProductField,
    ICheckAvailProductSlot,
} from "@interfaces";
import { AxiosResponse } from "axios";
import _ from "lodash";
import * as Moment from "moment";
import { extendMoment } from "moment-range";

const moment = extendMoment(Moment);

export const calculateTotal = (cartItems: IFareCart[] = [], isRRP: boolean): number => {
    return (cartItems || []).reduce((accumulator, cartItem) => {
        const quantity = cartItem.booking_quantity ?? 0;
        const apiProviderId = cartItem.product?.json?.apiProviderId ?? 0;
        const groupFaresForAvailabilityCheck = cartItem.product?.groupFaresForAvailabilityCheck;
        const farePrice = cartItem.availability?.FarePrice;
        const productPricing = cartItem.availability?.productPricingData;
        const price =
            apiProviderId > 0 && groupFaresForAvailabilityCheck
                ? ((isRRP ? farePrice?.RRP : farePrice?.NetRate) ?? 0)
                : ((isRRP ? productPricing?.RRP : productPricing?.NetRate) ?? 0);
        return accumulator + price * quantity;
    }, 0);
};

export const calculateProductPricingInfo = (cartItems: IFareCart[]): IPricingInfo => {
    const totalRRP = calculateTotal(cartItems, true);
    const totalNet = calculateTotal(cartItems, false);

    const commission = totalRRP - totalNet;
    const commissionPercent = totalRRP !== 0 ? (commission / totalRRP) * 100 : 0;
    const totalPrice = totalRRP;

    return {
        totalRRP,
        totalNet,
        commission,
        commissionPercent,
        totalPrice,
    };
};

export const getRRPOfAvailability = (availability: BaseAvailability): number | null => {
    switch (availability.availabilityType) {
        case AvailabilityType.ByRange:
            return (availability as IAvailabilityByRange).productPricingData.RRP;
        case AvailabilityType.ByProductAndRange:
            return (availability as IAvailabilityByProductAndRange).FarePrice?.RRP ?? null;
        default:
            throw Error("Unknown availability type");
    }
};

export const getProductPricesDetailsId = (availability: SelectedAvailability): string | null => {
    switch (availability.availabilityType) {
        case AvailabilityType.ByRange:
            return (availability as IAvailabilityByRange).productPricesDetailsId.toString();
        case AvailabilityType.ByProductAndRange:
            return (
                (
                    availability as IAvailabilityByProductAndRange
                ).FarePrice?.productPricesDetailsId?.toString() ?? null
            );
        default:
            throw new Error(`Faield to recognize availability type`, availability.availabilityType);
    }
};

export const calculateRRPOfAvailabilities = (
    availabilities: BaseAvailability[],
    quantity: number,
) => {
    return _.chain(availabilities)
        .filter((a) => getRRPOfAvailability(a) !== null)
        .map((a) => getRRPOfAvailability(a)! * quantity)
        .sum()
        .value();
};

export const convertCustomerInfoToData = (customerInfo: ICustomerInfo): ISetCustomerData => {
    return {
        title: customerInfo.title,
        firstName: customerInfo.first_name,
        lastName: customerInfo.last_name,
        dateOfBirth: customerInfo.date_of_birth,
        email: customerInfo.email,
        customerIndex: customerInfo.customer_index,
        phoneNumber: `${customerInfo.phone_number}`,
        postalCode: customerInfo.postal_code,
        countryCode: customerInfo.country_code,
    };
};

export const convertCustomerDataToInfo = (customerData: ISetCustomerData): ICustomerInfo => {
    return {
        title: customerData.title,
        first_name: customerData.firstName,
        last_name: customerData.lastName,
        date_of_birth: customerData.dateOfBirth,
        email: customerData.email,
        customer_index: customerData.customerIndex,
        phone_number: _.toNumber(customerData.phoneNumber),
        postal_code: customerData.postalCode,
        country_code: customerData.countryCode,
    };
};

const hasTravelTimes = (farePrices: ITourFare[]) =>
    _.every(farePrices, (fp) => !_.isNil(fp.travelStart) && !_.isNil(fp.travelEnd));

const hasSamePrices = (farePrices: ITourFare[]) =>
    _.chain(farePrices)
        .filter((fp) => fp.rrp > 0)
        .uniqBy((fp) => fp.rrp)
        .value().length === 1;

const hasSameLevy = (farePrices: ITourFare[]) =>
    _.chain(farePrices)
        .filter((fp) => !_.isEmpty(fp.levy))
        .filter((fp) => !_.isEmpty(fp.levyDescription))
        .uniqBy((fp) => `${fp.levy}-${fp.levyDescription}`)
        .value().length === 1;

const parseTravelMoment = (s: string) => {
    const iso = moment(s, moment.ISO_8601, true);
    if (iso.isValid()) return iso;
    const dmy = moment(s, "DD MMM YYYY", true);
    if (dmy.isValid()) return dmy;
    return moment(s);
};

const firstStartTravelTime = (travelStartTimes: string[]) =>
    _.chain(travelStartTimes)
        .map((ts) => parseTravelMoment(ts))
        .sortBy((ts) => ts.valueOf())
        .head()
        .value();

const lastEndTravelTime = (travelEndTimes: string[]) =>
    _.chain(travelEndTimes)
        .map((te) => parseTravelMoment(te))
        .sortBy((te) => -te.valueOf())
        .head()
        .value();

export const shouldEnableAvailabilityCheck = (
    checkFrom: Date,
    checkTo: Date,
    farePrices: ITourFare[],
) => {
    const allFarePricesHasTravelRange = hasTravelTimes(farePrices);
    if (!allFarePricesHasTravelRange) {
        return true; // some do not have travel range so allow to check availabliity
    }

    const firstAvailableStartDate = firstStartTravelTime(farePrices.map((fp) => fp.travelStart!));
    const lastAvailableEndDate = lastEndTravelTime(farePrices.map((fp) => fp.travelEnd!));

    const availableRange = moment.range(firstAvailableStartDate, lastAvailableEndDate);
    const checkRange = moment.range(checkFrom, checkTo);
    return !_.isNil(checkRange.intersect(availableRange));
};

export const shouldAllowAvailabilityCheck = (
    range: CheckProductAvailsDateRange,
    productPriceGroup: IPriceGroup,
) => {
    return shouldEnableAvailabilityCheck(
        range.startDate,
        moment(range.startDate).add(range.numDays, "days").toDate(),
        productPriceGroup.prices,
    );
};

export const formatBookingTime = (bookingTime: string) => {
    if (!moment(bookingTime).isValid()) {
        return bookingTime;
    }
    const [hour, minute] = bookingTime.split("_");
    return moment(`${hour}:${minute}`, "HH:mm").format("h:mm a");
};

export const availableTravelTimeMessage = (faresPrices: ITourFare[]): string | null => {
    const shouldShowTravelTimes = hasTravelTimes(faresPrices);
    if (!shouldShowTravelTimes) {
        return null;
    }
    const allowedStartTravelTime = firstStartTravelTime(faresPrices.map((fp) => fp.travelStart!));
    const allowedEndTravelTime = lastEndTravelTime(faresPrices.map((fp) => fp.travelEnd!));
    const isWithinSameYear = allowedStartTravelTime.year === allowedEndTravelTime.year;
    const timeFormat = `DD/MM${isWithinSameYear ? "/YYYY" : ""}`;
    return `${allowedStartTravelTime.format(timeFormat)} to ${allowedEndTravelTime.format(timeFormat)}`;
};

export const buildLevyMessage = (levy: number, levyDescription: string) =>
    `+ ${_.toNumber(levy).toFixed(2)} to pay on arrival ${levyDescription}`;

export const buildPriceGroup = (farePrice: ITourFare, farePricesInGroup: ITourFare[]) => {
    const hasVariablePricing = !hasSamePrices(farePricesInGroup);
    const rrpPrices = _.chain(farePricesInGroup).map((fp) => fp.rrp);
    const minRrp = rrpPrices.min().value();
    const maxRrp = rrpPrices.max().value();

    const rrp = hasVariablePricing
        ? `$${minRrp.toFixed(2)} - $${maxRrp.toFixed(2)}`
        : `$${farePrice.rrp.toFixed(2)}`;

    // Get rrpWas from the fare with minimum rrp
    const minRrpFare = farePricesInGroup.find((fp) => fp.rrp === minRrp) || farePrice;
    const rrpWas =
        minRrpFare?.rrpWas && minRrpFare.rrp < minRrpFare.rrpWas
            ? `$${minRrpFare.rrpWas.toFixed(2)}`
            : null;

    const levyMessage = !hasSameLevy(farePricesInGroup)
        ? null
        : buildLevyMessage(farePrice.levy!, farePrice.levyDescription!);
    const travelTimeMessage = !hasTravelTimes(farePricesInGroup)
        ? null
        : `Available from ${availableTravelTimeMessage(farePricesInGroup)}`;
    const priceGroup: IPriceGroup = {
        id: farePrice.fareTypeId.toString(),
        name: farePrice.fareName,
        hasVariablePricing: hasVariablePricing,
        prices: farePricesInGroup,
        rrp: rrp,
        rrpWas: rrpWas,
        levy: farePrice.levy ?? null,
        levyMessage: levyMessage,
        travelTimeMessage: travelTimeMessage,
        hasBookingTimeError: false,
        bookingSystem: farePrice.bookingSystem,
    };
    return priceGroup;
};

export { shouldCheckAvailabilityByGroup };

// Fares carrying these tags are not bookable "spaces" on a product: infant
// fares (typically free-of-charge lap infants) and add-ons (e.g. sleeping bag,
// picnic lunch) should never inflate the availability/space totals shown on the
// calendar. Matched case-insensitively against ITourFare.fareTags.
export const SPACE_COUNT_EXCLUDED_FARE_TAGS = ["infant", "addon"];

export const isExcludedFromSpaceCount = (priceGroup: IPriceGroup): boolean =>
    priceGroup.prices.some((fare) =>
        (fare.fareTags ?? []).some((tag) =>
            SPACE_COUNT_EXCLUDED_FARE_TAGS.includes(tag.trim().toLowerCase()),
        ),
    );

export { getFaresForDateRange } from "./fareDateRangeHelper";

export const faresPricesGroups = (faresPrices: ITourFare[]): IPriceGroup[] => {
    return _.chain(faresPrices)
        .groupBy((fp) => fp.fareTypeId)
        .map((farePrices) => {
            const farePrice = farePrices.at(0)!;
            return buildPriceGroup(farePrice, farePrices);
        })
        .value();
};

export const getMonthsInRange = (start: Date, end: Date) => {
    const startDate = moment(start).startOf("month");
    const endDate = moment(end).startOf("month");
    const months: moment.Moment[] = [];

    while (startDate.isSameOrBefore(endDate, "month")) {
        months.push(moment(startDate));
        startDate.add(1, "month"); // Move to the next month
    }

    return months;
};

export const getCalendarDays = (year: number, month: number, startOfWeek = "Sunday") => {
    const startDay = startOfWeek === "Sunday" ? 0 : 1; // 0 for Sunday, 1 for Monday

    // Get the first and last day of the target month
    const firstDay = moment({ year, month, day: 1 });
    const lastDay = moment(firstDay).endOf("month");

    // Find how many days to pad from the previous month
    const prevPadding = (firstDay.day() - startDay + 7) % 7;

    // Find how many days to pad from the next month
    const nextPadding = (7 - ((lastDay.day() - startDay + 1 + 7) % 7)) % 7;

    // Build the full calendar array
    const days = [];

    // Previous month padding
    for (let i = prevPadding; i > 0; i--) {
        days.push(moment(firstDay).subtract(i, "days"));
    }

    // Current month days
    for (let i = 0; i < lastDay.date(); i++) {
        days.push(moment(firstDay).add(i, "days"));
    }

    // Next month padding
    for (let i = 1; i <= nextPadding; i++) {
        days.push(moment(lastDay).add(i, "days"));
    }

    return days;
};

export const fieldToTimeFieldOption = (field: IAvailField): IProductAvailTimeOption => {
    return {
        type: field.type,
        displayName: field.displayName,
        required: field.required,
        date: moment(field.date, "YYYY-MM-DD").toDate(),
        options: field.options.map((fo) => {
            return {
                id: fo.externalId.toString(),
                displayName: fo.displayName,
                numAvailable: fo.numAvailable,
            };
        }),
    };
};

export const fieldToPickUpFieldOption = (
    timeId: string,
    field: IAvailField,
): IProductAvailPickUpOption => {
    return {
        timeId,
        type: field.type,
        displayName: field.displayName,
        required: field.required,
        options: field.options.map((fo) => {
            return {
                id: fo.externalId.toString(),
                displayName: fo.displayName,
                numAvailable: fo.numAvailable,
            };
        }),
    };
};

export const fieldToDropOffFieldOption = (
    timeId: string,
    field: IAvailField,
): IProductAvailDropOffOption => {
    return {
        timeId,
        type: field.type,
        displayName: field.displayName,
        required: field.required,
        options: field.options.map((fo) => {
            return {
                id: fo.externalId.toString(),
                displayName: fo.displayName,
                numAvailable: fo.numAvailable,
            };
        }),
    };
};

export const getBookingTimeIds = async (productPriceDetailIds: string[], bookingSystem: string) => {
    if (BOOKING_SYSTEMS_WITH_CUSTOM_BOOKING_TIME_IDS.includes(bookingSystem.trim().toLowerCase())) {
        return ["0"];
    } else {
        const bookingDetailsReqs = _.uniq(productPriceDetailIds).map(toursApi.getBookingDetail);
        const bookingDetailsResps = await Promise.allSettled(bookingDetailsReqs);
        bookingDetailsResps.filter((resp) => resp.status === "rejected").forEach(console.error);
        const uniqueBookingTimeIds = _.uniq(
            bookingDetailsResps
                .filter(
                    (resp): resp is PromiseFulfilledResult<AxiosResponse<ITourFareBookingTime>> =>
                        resp.status === "fulfilled",
                )
                .flatMap((resp) =>
                    resp.value.data.bookingTimes.map((bt) => bt.BookingTimeID.toString()),
                ),
        );
        return _.isEmpty(uniqueBookingTimeIds) ? ["0"] : uniqueBookingTimeIds;
    }
};

export const getBookingTravelOptions = async (
    productPricesDetailsId: string,
    fields: IAvailField[],
) => {
    const timeOptionFields: IProductAvailTimeOption[] = fields
        .filter((f) => f.name.toLowerCase() === "timeid")
        .map(fieldToTimeFieldOption);
    const pickUpOptionFields: IProductAvailPickUpOption[] = timeOptionFields.flatMap(
        (timeField) => {
            return timeField.options.flatMap((timeOption) => {
                return fields
                    .filter((f) => f.name.trim().toLowerCase() === "pickupid")
                    .map((f) => fieldToPickUpFieldOption(timeOption.id, f));
            });
        },
    );
    const dropOffOptionFields: IProductAvailPickUpOption[] = timeOptionFields.flatMap(
        (timeField) => {
            return timeField.options.flatMap((timeOption) => {
                return fields
                    .filter((f) => f.name.trim().toLowerCase() === "dropoff")
                    .map((f) => fieldToDropOffFieldOption(timeOption.id, f));
            });
        },
    );

    const confirmOptionsRequests = timeOptionFields.flatMap((timeField) => {
        return timeField.options.map((timeOption) =>
            toursApi
                .getDetailsToConfirmAvailability(
                    productPricesDetailsId,
                    timeField.date,
                    timeOption.id,
                )
                .then((av) => ({ timeId: timeOption.id, avails: av })),
        );
    });

    const confirmOptionsResponses = await Promise.allSettled(confirmOptionsRequests);
    const confirmPickUpOptionFields = confirmOptionsResponses
        .filter(
            (
                co,
            ): co is PromiseFulfilledResult<{
                timeId: string;
                avails: IAvailabilityDetailsToConfirm;
            }> => co.status === "fulfilled",
        )
        .flatMap((co) =>
            co.value.avails.fields
                .filter((f: IAvailField) => f.name.toLowerCase() === "pickupid")
                .map((f: IAvailField) => fieldToPickUpFieldOption(co.value.timeId, f)),
        );

    const confirmDropOffOptionFields = confirmOptionsResponses
        .filter(
            (
                co,
            ): co is PromiseFulfilledResult<{
                timeId: string;
                avails: IAvailabilityDetailsToConfirm;
            }> => co.status === "fulfilled",
        )
        .flatMap((co) =>
            co.value.avails.fields
                .filter((f: IAvailField) => f.name.toLowerCase() === "dropoffs")
                .map((f: IAvailField) => fieldToDropOffFieldOption(co.value.timeId, f)),
        );
    const confirmTimeOptionFields = confirmOptionsResponses
        .filter(
            (
                co,
            ): co is PromiseFulfilledResult<{
                timeId: string;
                avails: IAvailabilityDetailsToConfirm;
            }> => co.status === "fulfilled",
        )
        .flatMap((co) =>
            co.value.avails.fields
                .filter((f: IAvailField) => f.name.toLowerCase() === "timeid")
                .map((f: IAvailField) => fieldToTimeFieldOption(f)),
        );

    const ret: IAvailOptions = {
        timeOptions: timeOptionFields.concat(confirmTimeOptionFields),
        pickUpOptions: pickUpOptionFields.concat(confirmPickUpOptionFields),
        dropOffOptions: dropOffOptionFields.concat(confirmDropOffOptionFields),
    };
    return {
        ppdid: productPricesDetailsId,
        availOptions: ret,
    };
};

export const buildProductAvailability = (
    productId: string,
    bookingTimeId: string,
    availability: BaseAvailability,
    availbilityIndex: number,
    levy: number | null,
    travelOption: IAvailOptions | null,
): IProductAvailability => {
    const productAvailability = {
        productId,
        bookingTimeId,
        BookingDate: moment(availability.BookingDate).toDate(),
        NumAvailable: availability.NumAvailable,
        RRP: getRRPOfAvailability(availability),
        availIndex: availbilityIndex,
        levy,
        timeOptions: _.isNil(travelOption?.timeOptions) ? [] : travelOption!.timeOptions,
        pickUpOptions: _.isNil(travelOption?.pickUpOptions) ? [] : travelOption!.pickUpOptions,
        dropOffOptions: _.isNil(travelOption?.dropOffOptions) ? [] : travelOption!.dropOffOptions,
    };

    switch (availability.availabilityType) {
        case AvailabilityType.ByRange: {
            const availabilityByRange = availability as IAvailabilityByRange;
            return {
                ...productAvailability,
                productPricesDetailsId: availabilityByRange.productPricesDetailsId.toString(),
                NetRate: availabilityByRange.productPricingData.NetRate,
            };
        }
        case AvailabilityType.ByProductAndRange: {
            const availabilityByProductAndRange = availability as IAvailabilityByProductAndRange;
            return {
                ...productAvailability,
                productPricesDetailsId:
                    availabilityByProductAndRange.FarePrice?.productPricesDetailsId.toString() ??
                    null,
                NetRate: availabilityByProductAndRange.FarePrice?.NetRate ?? null,
            };
        }
        default:
            throw new Error("Unknown availability type");
    }
};

export const getBookingTimes = async (
    productId: string,
    farePrices: ITourFare[],
    apiProviderId: number,
    groupFaresForAvailabilityCheck: boolean,
    range: CheckProductAvailsDateRange,
): Promise<IBookingTimeData[]> => {
    const startDate = range.startDate;
    const endDate = moment(range.startDate).add(range.numDays, "day").toDate();
    const shouldCheckInGroup = shouldCheckAvailabilityByGroup(
        apiProviderId,
        groupFaresForAvailabilityCheck,
    );
    const farePricesWithBookingSystem = farePrices.filter((fp) => fp.hasBookingSystem);
    if (!shouldCheckInGroup) {
        const farePricesUniqByPPDIDAndBookingSystem = _.chain(farePricesWithBookingSystem)
            .map((fp) => ({ ...fp, bookingSystem: fp.bookingSystem! }))
            .uniqBy((fp) => `${fp.productPricesDetailsId}_${fp.bookingSystem}`)
            .value();
        const bookingDetailsReqs = farePricesUniqByPPDIDAndBookingSystem.map((fp) =>
            toursApi.getBookingDetail(fp.productPricesDetailsId).then((resp) => ({
                fareTypeId: fp.fareTypeId,
                productPricesDetailsId: fp.productPricesDetailsId,
                bookingDetails: resp.data,
            })),
        );
        const bookingDetailsResps = await Promise.allSettled(bookingDetailsReqs);
        bookingDetailsResps.filter((resp) => resp.status === "rejected").forEach(console.error);
        return bookingDetailsResps
            .filter((resp) => resp.status === "fulfilled")
            .map((resp) => (resp as PromiseFulfilledResult<IBookingTimeData>).value);
    } else {
        const farePricesUniqByFareTypeId = _.chain(farePricesWithBookingSystem)
            .map((fp) => fp.fareTypeId)
            .uniq()
            .value();
        const availabilitiesByProductReqs = farePricesUniqByFareTypeId.map((fp) =>
            toursApi.checkAvailByProductAndRange(productId, fp.toString(), startDate, endDate),
        );
        const availabilitiesByProductResps = await Promise.allSettled(availabilitiesByProductReqs);
        availabilitiesByProductResps
            .filter((resp) => resp.status === "rejected")
            .forEach(console.error);
        const availabilitiesByProduct = availabilitiesByProductResps
            .filter((resp) => resp.status === "fulfilled")
            .flatMap(
                (resp) => (resp as PromiseFulfilledResult<IAvailabilityByProductAndRange[]>).value,
            );
        const availabilitiesByProductUniqByPPDID = _.chain(availabilitiesByProduct)
            .uniqBy((avail) => avail.FarePrice!.productPricesDetailsId)
            .value();
        const bookingDetailsReqs = availabilitiesByProductUniqByPPDID.map((avail) =>
            toursApi.getBookingDetail(avail.FarePrice!.productPricesDetailsId).then((resp) => ({
                fareTypeId: avail.FarePrice!.fareTypeId,
                productPricesDetailsId: avail.FarePrice!.productPricesDetailsId,
                bookingDetails: resp.data,
            })),
        );
        const bookingDetailsResps = await Promise.allSettled(bookingDetailsReqs);
        bookingDetailsResps.filter((resp) => resp.status === "rejected").forEach(console.error);
        return bookingDetailsResps
            .filter((resp) => resp.status === "fulfilled")
            .map((resp) => (resp as PromiseFulfilledResult<IBookingTimeData>).value);
    }
};

const isBookingTimeValid = (bookingDate: string, bookingTimeId: string): boolean => {
    const today = moment().format("DD-MMM-YYYY");
    if (bookingDate !== today) {
        return true; // Future dates are always valid
    }

    // For today's date, check if booking time has passed
    const [hours, minutes] = bookingTimeId.split("_").map(Number);
    const bookingTime = moment().startOf("day").add(hours, "hours").add(minutes, "minutes");
    const now = moment();

    return bookingTime.isAfter(now);
};

export const getBookingAvailabilities = async ({
    startDate,
    numDays,
    selectedBookingTimeId,
    productId,
    bookingPriceGroup: productPriceGroup,
    apiProviderId,
    groupFaresForAvailabilityCheck,
    bookingTime,
}: CheckProductAvailsData): Promise<IBookingAvailability[]> => {
    const shouldCheckInGroup = shouldCheckAvailabilityByGroup(
        apiProviderId,
        groupFaresForAvailabilityCheck,
    );
    if (shouldCheckInGroup) {
        const avails = await toursApi
            .checkAvailByProductAndRange(
                productId,
                productPriceGroup.id,
                startDate,
                moment(startDate).add(numDays, "days").toDate(),
            )
            .then((avails) =>
                avails.map((a) => ({ ...a, availabilityType: AvailabilityType.ByProductAndRange })),
            );
        // for some products booking time is not available
        return avails.filter((avail) => isBookingTimeValid(avail.BookingDate, bookingTime || ""));
    }
    const productPriceGroupAvailsReqs = productPriceGroup.prices.map((fp) =>
        toursApi
            .checkAvailRange(
                fp.productPricesDetailsId.toString(),
                selectedBookingTimeId,
                startDate,
                numDays,
            )
            .then((avails) =>
                avails.map((a) => ({ ...a, availabilityType: AvailabilityType.ByRange })),
            ),
    );
    const productPriceGroupAvailsResps = await Promise.allSettled(productPriceGroupAvailsReqs);
    productPriceGroupAvailsResps
        .filter((resp) => resp.status === "rejected")
        .forEach(console.error);
    const allAvails = productPriceGroupAvailsResps
        .filter((resp) => resp.status === "fulfilled")
        .flatMap((resp) => (resp as PromiseFulfilledResult<IBookingAvailability[]>).value);
    // for some products booking time is not available
    return allAvails.filter((avail) => isBookingTimeValid(avail.BookingDate, bookingTime || ""));
};

export interface FareGroupAvailabilityResult {
    priceGroup: IPriceGroup;
    bookingTimes: Array<{ id: string; name: string }>;
    availabilities: IBookingAvailability[];
}

export const getBookingTimesForFareGroups = async (
    priceGroups: IPriceGroup[],
): Promise<Array<{ id: string; name: string }>> => {
    const groupResults = await Promise.allSettled(
        priceGroups.map(async (priceGroup) => {
            const seenBtIds = new Set<string>();
            let bookingTimes: Array<{ id: string; name: string }> = [];

            priceGroup.prices.forEach((price) => {
                if (price.bookingTimes && price.bookingTimes.length > 0) {
                    price.bookingTimes.forEach((bt) => {
                        const id = bt.BookingTimeID.toString();
                        if (!seenBtIds.has(id) && bt.BookingTime !== "Daily availability") {
                            seenBtIds.add(id);
                            bookingTimes.push({ id, name: bt.BookingTime });
                        }
                    });
                }
            });

            if (bookingTimes.length === 0) {
                const firstPrice = priceGroup.prices[0];
                if (firstPrice) {
                    const bookingDetailResp = await toursApi.getBookingDetail(firstPrice.productPricesDetailsId);
                    const bts = bookingDetailResp.data.bookingTimes;
                    const isDaily = bts.length === 1 && bts[0].BookingTime === "Daily availability";
                    if (!isDaily) {
                        bookingTimes = bts.map((bt) => ({
                            id: bt.BookingTimeID.toString(),
                            name: bt.BookingTime,
                        }));
                    }
                }
            }

            return bookingTimes;
        }),
    );

    const allTimes: Array<{ id: string; name: string }> = [];
    const seenIds = new Set<string>();
    groupResults.forEach((result) => {
        if (result.status === "fulfilled") {
            result.value.forEach((bt) => {
                if (!seenIds.has(bt.id)) {
                    seenIds.add(bt.id);
                    allTimes.push(bt);
                }
            });
        }
    });

    return allTimes;
};

// Resolves the booking `days` value for an addCart payload from a product's
// configured duration. The backend treats `days` as the length of the booking
// (not a query window), so multi-day products (e.g. a 2-day/1-night overnight
// tour) MUST send their full duration or the availability confirmation fails
// with "fare unavailable". durationDays arrives as a string or number and may
// be missing/0 for single-day products, so we coerce and floor at 1. This
// mirrors the trip planner's `Math.max(1, durationDays)` so every add path
// books the same span.
export const resolveBookingDays = (
    durationDays?: string | number | null,
): number => {
    const parsed = Number(durationDays);
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1;
};

/**
 * Re-bases a calendar row's `availIndex` so it is relative to the booking's
 * `startDate`, using ONLY the already-loaded `checkAvailabilityProduct` rows
 * that build the calendar — no extra network call.
 *
 * Why this is needed: the calendar's `availIndex` is the row's position in the
 * fare's whole loaded availability window (a late date can land at e.g. index
 * 21). But the FT `/cart/items/v2` endpoint re-derives availability anchored at
 * the payload's `startDate` (which we set to the booking date) and reads the row
 * at each `selectedAvailableIndices` entry. So the index must be relative to the
 * first available row on/after `startDate`. Because the availability array is
 * date-ascending and gap-free, that relative index is simply
 * `targetAvailIndex - (index of the first row whose date >= startDate)`. For the
 * common single-date booking (startDate === bookingDate) this is 0.
 *
 * `rows` are the fare's calendar rows ({ date, availIndex }); `startDate` and
 * each row's `date` may be "DD-MMM-YYYY" or "YYYY-MM-DD". Returns -1 when no row
 * falls on/after `startDate` (or the result would be negative) so callers can
 * fall back to the raw index.
 */
export const anchorIndexToStartDate = (
    rows: Array<{ date?: string | null; availIndex: number }>,
    startDate: string,
    targetAvailIndex: number,
): number => {
    const formats = ["DD-MMM-YYYY", "YYYY-MM-DD", moment.ISO_8601];
    const start = moment(startDate, formats, true).isValid()
        ? moment(startDate, formats, true)
        : moment(startDate);
    let base = Number.POSITIVE_INFINITY;
    rows.forEach((r) => {
        if (!r?.date) return;
        const d = moment(r.date, formats, true).isValid()
            ? moment(r.date, formats, true)
            : moment(r.date);
        if (d.isSameOrAfter(start, "day") && r.availIndex < base) {
            base = r.availIndex;
        }
    });
    if (!Number.isFinite(base)) return -1;
    const rebased = targetAvailIndex - base;
    return rebased >= 0 ? rebased : -1;
};

// The checkAvailabilityProduct endpoint returns dates as "YYYY-MM-DD", but the
// rest of the availability/calendar flow expects "DD-MMM-YYYY" (the format the
// legacy range endpoints returned). Normalise centrally so downstream date
// parsing (e.g. moment(date, "DD-MMM-YYYY") in the calendar panel) stays valid.
const normalizeAvailabilityDate = (raw: string): string => {
    if (!raw) return raw;
    const parsed = moment(
        raw,
        ["YYYY-MM-DD", CHECK_AVAILABILITY_START_DATE_FORMAT, moment.ISO_8601],
        true,
    );
    return parsed.isValid() ? parsed.format(CHECK_AVAILABILITY_START_DATE_FORMAT) : raw;
};

// Combines slot-level and fare-level additionalFields into a single list.
// Slot-level entries are the more specific source, so they win when the same
// additionalFieldId appears at both levels; any field present at only one level
// is still included. Preserves slot-first ordering.
const mergeAdditionalFieldsById = (
    slotFields: ICheckAvailProductField[] | undefined,
    fareFields: ICheckAvailProductField[] | undefined,
): ICheckAvailProductField[] => {
    const seen = new Set<string>();
    const out: ICheckAvailProductField[] = [];
    [...(slotFields ?? []), ...(fareFields ?? [])].forEach((field) => {
        const id = String(field.additionalFieldId);
        if (seen.has(id)) return;
        seen.add(id);
        out.push(field);
    });
    return out;
};

const mapAdditionalFields = (
    fields: ICheckAvailProductField[] | undefined,
    date: string,
    timeId: string,
): IAvailField[] =>
    (fields ?? []).map((field) => ({
        date,
        timeId,
        type: field.type,
        name: field.additionalFieldId,
        displayName: field.label,
        required: field.required,
        options: (field.options ?? []).map((opt) => ({
            externalId: opt.externalId,
            name: opt.name ?? "",
            displayName: opt.displayName,
            // numAvailable is only present for some booking systems; consumers
            // already guard against it being undefined/null.
            numAvailable: opt.numAvailable as number,
            note: opt.note ?? null,
        })),
    }));

const slotToAvailability = (
    slot: ICheckAvailProductSlot,
    fare: ICheckAvailProductFare,
): IAvailabilityByRange => {
    const bookingDate = normalizeAvailabilityDate(slot.bookingDate);
    return {
        BookingDate: bookingDate,
        BookingTimeID: !_.isNil(slot.bookingTimeId) ? String(slot.bookingTimeId) : null,
        BookingTime: slot.bookingTime ?? null,
        NumAvailable: slot.numAvailable,
        DateRestriction: null,
        Note: null,
        availabilityType: AvailabilityType.ByRange,
        EndDate: null,
        productPricesDetailsId: fare.productPricesDetailsId,
        datePriceCacheId: slot.datePriceCacheId ?? null,
        RequiresFurtherDetailsToConfirm: false,
        productPricingData: {
            // The endpoint does not return NetRate; net/commission is sourced
            // from the FT cart at checkout. RRP drives the calendar pricing display.
            RRP: slot.rrp,
            NetRate: slot.rrp,
            FareCurrencyRRP: slot.rrp,
            FareCurrencyNetRate: slot.rrp,
            IgnorePpdMargins: false,
        },
        fields: mapAdditionalFields(
            // Merge slot-level and fare-level additionalFields. RespaxV2 products
            // (e.g. Uncle Brians) return the pickup/lodging selector once at the
            // fare level (a sibling of `dates`) instead of inside every slot, so
            // reading only the slot silently dropped it and the pickup selector
            // never rendered. Slot-level fields win on a duplicate
            // additionalFieldId (they are the more specific source), while any
            // fare-level-only field (e.g. `lodgings`) is still surfaced.
            mergeAdditionalFieldsById(slot.additionalFields, fare.additionalFields),
            bookingDate,
            String(slot.bookingTimeId ?? "0"),
        ),
    };
};

/**
 * Builds a price group directly from a `checkAvailabilityProduct` response fare.
 * Used only when the product's configured price groups fail to match any response
 * fare by productPricesDetailsId (e.g. a whole-product sale shifts every PPD), so
 * the calendar can still populate from the response instead of rendering empty.
 * The response carries the fare name, RRP and PPD, so each derived group is
 * correctly named and self-contained (no availability is borrowed across fares).
 * numPax is not part of the response, so it defaults to 1 via buildDisplayRows.
 */
const buildResponseDerivedPriceGroup = (
    fare: ICheckAvailProductFare,
    bookingSystem: string,
): IPriceGroup => ({
    id: String(fare.productPricesDetailsId),
    name: fare.fareName,
    hasVariablePricing: false,
    rrp: `$${(fare.rrp ?? 0).toFixed(2)}`,
    rrpWas: null,
    travelTimeMessage: null,
    levy: null,
    levyMessage: null,
    hasBookingTimeError: false,
    bookingSystem,
    prices: [
        {
            productPricesDetailsId: fare.productPricesDetailsId,
            fareTypeId: String(fare.productPricesDetailsId),
            fareName: fare.fareName,
            rrp: fare.rrp,
            numPax: "1",
            bookingSystem,
        } as unknown as ITourFare,
    ],
});

/**
 * Maps a single `checkAvailabilityProduct` response into the per-fare-group
 * availability shape consumed by the calendar. Each price group receives only
 * the availability of the fares it owns (matched by productPricesDetailsId), so
 * all fares come back together without requiring a fare to be selected first.
 *
 * Preserves the existing `selectedBookingTimeId` semantics: when the selected
 * time exists it is the only one returned, otherwise it falls back to all times.
 */
export const mapCheckAvailProductToFareGroups = (
    response: ICheckAvailabilityProduct,
    priceGroups: IPriceGroup[],
    selectedBookingTimeId?: string,
): FareGroupAvailabilityResult[] => {
    const responseFares = response?.fares ?? [];
    const faresByPpdId = new Map<string, ICheckAvailProductFare>();
    responseFares.forEach((fare) => {
        faresByPpdId.set(String(fare.productPricesDetailsId), fare);
    });

    // Associate each price group with its response fares by
    // productPricesDetailsId, recording which response fares were claimed so we
    // can recover any that no group matched.
    const claimedPpdIds = new Set<string>();
    const groupFares = priceGroups.map((priceGroup) => {
        const matchedFares = priceGroup.prices
            .map((p) => faresByPpdId.get(String(p.productPricesDetailsId)))
            .filter((f): f is ICheckAvailProductFare => f !== undefined);
        matchedFares.forEach((f) => claimedPpdIds.add(String(f.productPricesDetailsId)));
        return { priceGroup, fares: matchedFares };
    });

    // The checkAvailabilityProduct response is the source of truth for what is
    // bookable, and its fare PPDs can legitimately differ from the product's
    // configured fare PPDs (e.g. a promotional/sale fare carries its own
    // productPricesDetailsId). When response fares are not claimed by any price
    // group, populate the calendar from the response instead of dropping their
    // availability. The strategy depends on how the join failed (see below).
    const orphanFares = responseFares.filter(
        (f) => !claimedPpdIds.has(String(f.productPricesDetailsId)),
    );

    let workingGroups = groupFares;
    if (orphanFares.length > 0) {
        const inheritedBookingSystem = priceGroups[0]?.bookingSystem ?? "";
        // Response fares with no dates (e.g. a placeholder "Allocation" fare) add
        // only empty rows, so they are never surfaced as their own group.
        const bookableOrphans = orphanFares.filter(
            (f) => Object.keys(f.dates ?? {}).length > 0,
        );
        if (groupFares.length === 1) {
            // Single configured group: the association is unambiguous, so attach
            // the orphans to it and keep its metadata (name, numPax, etc.).
            groupFares[0].fares = [...groupFares[0].fares, ...orphanFares];
        } else if (claimedPpdIds.size === 0) {
            // Multiple configured groups but NONE matched any response fare (a
            // total PPD join failure, e.g. a whole-product sale shifted every
            // PPD). The configured groups are disconnected from what is actually
            // bookable, so derive the calendar groups from the response fares
            // themselves. Each response fare becomes its own correctly-named
            // group, so availability still shows without guessing which
            // configured group an orphan belongs to.
            workingGroups = bookableOrphans.map((f) => ({
                priceGroup: buildResponseDerivedPriceGroup(f, inheritedBookingSystem),
                fares: [f],
            }));
        } else {
            // Partial match: some configured groups matched, but other response
            // fares are orphaned. An orphan here is a legitimately bookable fare
            // carrying its own productPricesDetailsId that no configured group
            // owns (e.g. a CPR "Shared Single" returned alongside matched Private
            // Double/Twin fares whose configured PPDs are all a different, out-of-
            // window season). Surface each orphan as its own correctly-named
            // response-derived group rather than dropping it. Using a separate
            // group (named by the response fareName) preserves the matched groups
            // and avoids force-fitting one fare's availability under another
            // fare's name.
            workingGroups = [
                ...groupFares,
                ...bookableOrphans.map((f) => ({
                    priceGroup: buildResponseDerivedPriceGroup(f, inheritedBookingSystem),
                    fares: [f],
                })),
            ];
        }
    }

    return workingGroups.map(({ priceGroup, fares }) => {
        const seenTimeIds = new Set<string>();
        let bookingTimes: Array<{ id: string; name: string }> = [];
        const allAvailabilities: IAvailabilityByRange[] = [];

        fares.forEach((fare) => {
            Object.values(fare.dates ?? {}).forEach((slots) => {
                (slots ?? []).forEach((slot) => {
                    // The endpoint can return null entries inside a date's slot
                    // array (e.g. a day present in the map but with no real slot).
                    // Skip them so a single null doesn't reject the whole fetch.
                    if (_.isNil(slot)) {
                        return;
                    }
                    const timeId = String(slot.bookingTimeId ?? "0");
                    const timeName = slot.bookingTime ?? "";
                    if (timeName && timeName !== "Daily availability" && !seenTimeIds.has(timeId)) {
                        seenTimeIds.add(timeId);
                        bookingTimes.push({ id: timeId, name: timeName });
                    }
                    allAvailabilities.push(slotToAvailability(slot, fare));
                });
            });
        });

        let availabilities: IBookingAvailability[] = allAvailabilities;
        if (selectedBookingTimeId) {
            const match = bookingTimes.find((bt) => bt.id === selectedBookingTimeId);
            if (match) {
                bookingTimes = [match];
                availabilities = allAvailabilities.filter(
                    (a) => String(a.BookingTimeID ?? "0") === selectedBookingTimeId,
                );
            }
        }

        // De-duplicate by date + time (overlapping fare periods can repeat a
        // slot); keep the first occurrence to preserve a stable availIndex order.
        const seen = new Set<string>();
        const deduped: IBookingAvailability[] = [];
        availabilities.forEach((a) => {
            const key = `${a.BookingDate}_${a.BookingTimeID ?? "0"}`;
            if (!seen.has(key)) {
                seen.add(key);
                deduped.push(a);
            }
        });

        return { priceGroup, bookingTimes, availabilities: deduped };
    });
};

/**
 * Maps the `checkAvailabilityByProductAndRange` response for a single price group
 * into the shared FareGroupAvailabilityResult shape consumed by the calendar.
 * Mirrors `mapCheckAvailProductToFareGroups`' booking-time derivation, selected
 * time filtering and date+time de-duplication so the two availability sources
 * render identically.
 */
export const mapAvailsToFareGroup = (
    priceGroup: IPriceGroup,
    avails: IBookingAvailability[],
    selectedBookingTimeId?: string,
): FareGroupAvailabilityResult => {
    const seenTimeIds = new Set<string>();
    let bookingTimes: Array<{ id: string; name: string }> = [];
    avails.forEach((a) => {
        const id = String(a.BookingTimeID ?? "0");
        const name = a.BookingTime ?? "";
        if (name && name !== "Daily availability" && !seenTimeIds.has(id)) {
            seenTimeIds.add(id);
            bookingTimes.push({ id, name });
        }
    });

    let availabilities: IBookingAvailability[] = avails;
    if (selectedBookingTimeId) {
        const match = bookingTimes.find((bt) => bt.id === selectedBookingTimeId);
        if (match) {
            bookingTimes = [match];
            availabilities = avails.filter(
                (a) => String(a.BookingTimeID ?? "0") === selectedBookingTimeId,
            );
        }
    }

    const seen = new Set<string>();
    const deduped: IBookingAvailability[] = [];
    availabilities.forEach((a) => {
        const key = `${a.BookingDate}_${a.BookingTimeID ?? "0"}`;
        if (!seen.has(key)) {
            seen.add(key);
            deduped.push(a);
        }
    });

    return { priceGroup, bookingTimes, availabilities: deduped };
};

export const mapProductAndRangeToFareGroup = (
    priceGroup: IPriceGroup,
    avails: IAvailabilityByProductAndRange[],
    selectedBookingTimeId?: string,
): FareGroupAvailabilityResult =>
    mapAvailsToFareGroup(priceGroup, avails, selectedBookingTimeId);

/**
 * Accommodation availability path. The endpoint is chosen with the same
 * SiteMinder-vs-SOA discriminator the checkout flow uses
 * (`shouldCheckAvailabilityByGroup`, i.e. `apiProviderId > 0 &&
 * groupFaresForAvailabilityCheck`):
 *
 * - SiteMinder accommodation → `checkAvailabilityByProductAndRange`
 *   (`/checkAvailabilityByProductAndRange/{productId}/{fareTypeId}/{startDate}/{endDate}`),
 *   one call per room/fare group across the requested night range.
 * - SOA / Generic accommodation → `checkavailabilityrange`
 *   (`/checkavailabilityrange/{productPricesDetailsId}/{timeId}/{startDate}/{days}`),
 *   one call per fare price, mirroring the non-grouped checkout path.
 *
 * Groups run concurrently and partial failures are tolerated (a single failing
 * room never blanks the whole calendar); only an all-groups failure rejects.
 */
export const getAccommodationFaresAvailabilities = async ({
    priceGroups,
    startDate,
    numDays,
    productId,
    apiProviderId,
    groupFaresForAvailabilityCheck,
    selectedBookingTimeId,
}: {
    priceGroups: IPriceGroup[];
    startDate: Date;
    numDays: number;
    productId: string;
    apiProviderId: number;
    groupFaresForAvailabilityCheck: boolean;
    selectedBookingTimeId?: string;
}): Promise<FareGroupAvailabilityResult[]> => {
    const useByProductAndRange = shouldCheckAvailabilityByGroup(
        apiProviderId,
        groupFaresForAvailabilityCheck,
    );
    const endDate = moment(startDate).add(numDays, "days").toDate();

    const buildSiteMinderGroup = (priceGroup: IPriceGroup) =>
        toursApi
            .checkAvailByProductAndRange(productId, priceGroup.id, startDate, endDate)
            .then((avails) => mapAvailsToFareGroup(priceGroup, avails, selectedBookingTimeId));

    const buildSoaGroup = async (priceGroup: IPriceGroup) => {
        // The non-grouped checkout path keys the range call on each fare price's
        // productPricesDetailsId, defaulting to the "0" (daily) time slot when no
        // departure time is selected on the calendar.
        const timeId = selectedBookingTimeId ?? "0";
        const perPrice = await Promise.allSettled(
            priceGroup.prices.map((price) =>
                toursApi.checkAvailRange(
                    price.productPricesDetailsId.toString(),
                    timeId,
                    startDate,
                    numDays,
                ),
            ),
        );

        perPrice
            .filter((r) => r.status === "rejected")
            .forEach((r) =>
                console.error(
                    "[AllFaresAvail] checkavailabilityrange FAILED:",
                    (r as PromiseRejectedResult).reason,
                ),
            );

        const fulfilled = perPrice.filter(
            (r): r is PromiseFulfilledResult<IAvailabilityByRange[]> => r.status === "fulfilled",
        );

        // Reject the group only when every fare price failed, so one bad room
        // never blanks the rest of the group's availability.
        if (fulfilled.length === 0 && priceGroup.prices.length > 0) {
            throw new Error(`Failed to load availability for ${priceGroup.name}`);
        }

        const avails = fulfilled.flatMap((r) => r.value);
        return mapAvailsToFareGroup(priceGroup, avails, selectedBookingTimeId);
    };

    const settled = await Promise.allSettled(
        priceGroups.map((priceGroup) =>
            useByProductAndRange ? buildSiteMinderGroup(priceGroup) : buildSoaGroup(priceGroup),
        ),
    );

    settled
        .filter((r) => r.status === "rejected")
        .forEach((r) =>
            console.error(
                "[AllFaresAvail] accommodation availability group FAILED:",
                (r as PromiseRejectedResult).reason,
            ),
        );

    const fulfilled = settled
        .filter((r) => r.status === "fulfilled")
        .map((r) => (r as PromiseFulfilledResult<FareGroupAvailabilityResult>).value);

    if (fulfilled.length === 0 && priceGroups.length > 0) {
        throw new Error("Failed to load accommodation availability");
    }

    return fulfilled;
};

export const getAllFaresAvailabilities = async ({
    priceGroups,
    startDate,
    numDays,
    productId,
    apiProviderId,
    groupFaresForAvailabilityCheck,
    selectedBookingTimeId,
    isAccommodation,
}: {
    priceGroups: IPriceGroup[];
    startDate: Date;
    numDays: number;
    productId: string;
    apiProviderId: number;
    groupFaresForAvailabilityCheck: boolean;
    selectedBookingTimeId?: string;
    isAccommodation?: boolean;
}): Promise<FareGroupAvailabilityResult[]> => {
    if (isAccommodation) {
        const accommodationEndpoint = shouldCheckAvailabilityByGroup(
            apiProviderId,
            groupFaresForAvailabilityCheck,
        )
            ? "checkAvailabilityByProductAndRange – SiteMinder"
            : "checkavailabilityrange – SOA";
        console.log(
            `[AllFaresAvail] Starting fetch (${accommodationEndpoint} – accommodation)`,
            {
                productId,
                apiProviderId,
                groupFaresForAvailabilityCheck,
                priceGroupCount: priceGroups.length,
                priceGroupNames: priceGroups.map((g) => g.name),
                startDate: moment(startDate).format("DD-MMM-YYYY"),
                numDays,
                selectedBookingTimeId: selectedBookingTimeId ?? "(all)",
            },
        );
        return getAccommodationFaresAvailabilities({
            priceGroups,
            startDate,
            numDays,
            productId,
            apiProviderId,
            groupFaresForAvailabilityCheck,
            selectedBookingTimeId,
        });
    }

    console.log("[AllFaresAvail] Starting fetch (checkAvailabilityProduct)", {
        productId,
        priceGroupCount: priceGroups.length,
        priceGroupNames: priceGroups.map((g) => g.name),
        startDate: moment(startDate).format("DD-MMM-YYYY"),
        numDays,
        selectedBookingTimeId: selectedBookingTimeId ?? "(all)",
    });

    try {
        const response = await toursApi.checkAvailabilityProduct(productId, startDate, numDays);
        const results = mapCheckAvailProductToFareGroups(response, priceGroups, selectedBookingTimeId);
        console.log(
            `[AllFaresAvail] Complete: mapped ${results.length} fare group(s) from a single call`,
            results.map((r) => `${r.priceGroup.name}: ${r.availabilities.length} avail, ${r.bookingTimes.length} times`),
        );
        return results;
    } catch (e) {
        console.error("[AllFaresAvail] checkAvailabilityProduct FAILED:", e);
        throw e;
    }
};

/**
 *
 * @param farePrices
 * @param selectedFareTypeIndex
 * @param startDate
 * @param endDate
 * @param apiProviderId
 * @param groupFaresForAvailabilityCheck
 * @param productId
 */
export const getAvailabilities = async (
    farePrices: ITourFare[],
    selectedFareTypeIndex: number,
    startDate: Date,
    endDate: Date,
    apiProviderId: number,
    groupFaresForAvailabilityCheck: boolean,
    productId: string,
): Promise<IProductAvailability[]> => {
    const shouldCheckInGroup = shouldCheckAvailabilityByGroup(
        apiProviderId,
        groupFaresForAvailabilityCheck,
    );
    const farePrice = farePrices[selectedFareTypeIndex];
    let productPriceDetailIds: string[] = [];
    if (!shouldCheckInGroup) {
        productPriceDetailIds = farePrices
            .filter((fp) => fp.fareTypeId === farePrice.fareTypeId)
            .filter((fp) => {
                const selectedRange = moment.range(startDate, endDate);
                if (!_.isNil(fp.travelStart) && !_.isNil(fp.travelEnd)) {
                    const travelRange = moment.range(
                        moment(fp.travelStart, FARE_PRICE_TRAVEL_DATE_FORMAT).toDate(),
                        moment(fp.travelEnd, FARE_PRICE_TRAVEL_DATE_FORMAT).toDate(),
                    );
                    return !_.isNil(selectedRange.intersect(travelRange));
                }

                if (!_.isNil(fp.travelStart)) {
                    return selectedRange.contains(
                        moment(fp.travelStart, FARE_PRICE_TRAVEL_DATE_FORMAT).toDate(),
                    );
                }

                if (!_.isNil(fp.travelEnd)) {
                    return selectedRange.contains(
                        moment(fp.travelEnd, FARE_PRICE_TRAVEL_DATE_FORMAT).toDate(),
                    );
                }

                // allow ppdid if both is absent
                return true;
            })
            .map((fp) => fp.productPricesDetailsId.toString());
        const bookingTimeIds = await getBookingTimeIds(
            productPriceDetailIds,
            farePrice.bookingSystem,
        );
        const availabilitiesReq = await bookingTimeIds.flatMap(async (bookingTimeId) => {
            const availRangesReqs = productPriceDetailIds.map((ppdid) =>
                toursApi.checkAvailRange(
                    ppdid.toString(),
                    bookingTimeId,
                    startDate,
                    moment(endDate).diff(startDate, "days"),
                ),
            );
            const availRangesResps = await Promise.allSettled(availRangesReqs);
            const availRangesData = availRangesResps
                .filter(
                    (resp): resp is PromiseFulfilledResult<IAvailabilityByRange[]> =>
                        resp.status === "fulfilled",
                )
                .flatMap((resp) => resp.value);
            const travelOptionsReqs = availRangesData.flatMap((a) =>
                getBookingTravelOptions(a.productPricesDetailsId!.toString(), a.fields),
            );
            const travelOptionsResps = await Promise.allSettled(travelOptionsReqs);
            travelOptionsResps.filter((resp) => resp.status === "rejected").forEach(console.error);
            const travelOptions = travelOptionsResps
                .filter(
                    (
                        resp,
                    ): resp is PromiseFulfilledResult<{
                        ppdid: string;
                        availOptions: IAvailOptions;
                    }> => resp.status === "fulfilled",
                )
                .map((resp) => resp.value);
            return availRangesData.map((availData, availIndex) => {
                const travelOption = travelOptions.find(
                    (t) => t.ppdid === availData.productPricesDetailsId?.toString(),
                )?.availOptions;
                return buildProductAvailability(
                    productId,
                    bookingTimeId,
                    availData,
                    availIndex,
                    farePrice.levy ?? null,
                    travelOption ?? null,
                );
            });
        });
        const availabilitiesResp = await Promise.allSettled(availabilitiesReq);
        return availabilitiesResp
            .filter(
                (resp): resp is PromiseFulfilledResult<IProductAvailability[]> =>
                    resp.status === "fulfilled",
            )
            .flatMap((resp) => resp.value);
    } else {
        const availabilitiesByProductReq = await toursApi.checkAvailByProductAndRange(
            productId,
            farePrice.fareTypeId.toString(),
            startDate,
            endDate,
        );
        const availabilitiesByProduct = availabilitiesByProductReq;
        productPriceDetailIds = _.chain(availabilitiesByProduct)
            .filter((a) => !_.isNil(a.FarePrice?.productPricesDetailsId))
            .map((a) => a.FarePrice!.productPricesDetailsId.toString())
            .uniq()
            .value();
        const bookingTimeIds = await getBookingTimeIds(
            productPriceDetailIds,
            farePrice.bookingSystem,
        );
        const availabilitiesReq = await bookingTimeIds.flatMap(async (bookingTimeId) => {
            const availRangesReqs = productPriceDetailIds.map((ppdid) =>
                toursApi.checkAvailRange(
                    ppdid.toString(),
                    bookingTimeId,
                    startDate,
                    moment(endDate).diff(startDate, "days"),
                ),
            );
            const availRangesResps = await Promise.allSettled(availRangesReqs);
            const availRangesData = availRangesResps
                .filter(
                    (resp): resp is PromiseFulfilledResult<IAvailabilityByRange[]> =>
                        resp.status === "fulfilled",
                )
                .flatMap((resp) => resp.value);
            const travelOptionsReqs = availRangesData.flatMap((a) =>
                getBookingTravelOptions(a.productPricesDetailsId!.toString(), a.fields),
            );
            const travelOptionsResps = await Promise.allSettled(travelOptionsReqs);
            travelOptionsResps.filter((resp) => resp.status === "rejected").forEach(console.error);
            const travelOptions = travelOptionsResps
                .filter(
                    (
                        resp,
                    ): resp is PromiseFulfilledResult<{
                        ppdid: string;
                        availOptions: IAvailOptions;
                    }> => resp.status === "fulfilled",
                )
                .map((resp) => resp.value);

            return availabilitiesByProduct.map((availData, availIndex) => {
                const travelOption = travelOptions.find(
                    (t) => t.ppdid === availData.FarePrice?.productPricesDetailsId?.toString(),
                )?.availOptions;
                return buildProductAvailability(
                    productId,
                    bookingTimeId,
                    availData,
                    availIndex,
                    farePrice.levy ?? null,
                    travelOption ?? null,
                );
            });
        });
        const availabilitiesResp = await Promise.allSettled(availabilitiesReq);
        availabilitiesResp.filter((resp) => resp.status === "rejected").forEach(console.error);
        return availabilitiesResp
            .filter(
                (resp): resp is PromiseFulfilledResult<IProductAvailability[]> =>
                    resp.status === "fulfilled",
            )
            .flatMap((resp) => resp.value);
    }
};

export const groupCartItems = (items: IFareCart[]) => _.groupBy(items, (item) => item.group_id);
